<template>
  <div>
    <component
      :is="currentComponent"
      v-if="currentComponent && (screenPartialData || signupScreen.isInitialScreen())"
      :signup-screen="signupScreen"
      :session-info="sessionInfo"
      v-bind="screenPartialData?.signupPartialViewsSectionBuilder?.sectionBuilder[0]"
      :screen-partial-data="screenPartialDataWithRemainingComponents"
      :signup-survey-div="signupSurveyDiv"
      @update-page="onUpdatePage"
      @hide-next-button="onHideNextButton"
      @set-email-status="onSetEmailStatus"
      @hide-back-button="onHideBackButton"
    >
      <slot />
    </component>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { SignupScreen } from './Form/coordinator/UnifiedSignup/screens/SignupScreen';
import type UnifiedSignupSessionInfo from './Form/coordinator/UnifiedSignup/session-info/UnifiedSignupSessionInfo';
import type { UnifiedSignupScreenName } from './Form/coordinator/UnifiedSignup/UnifiedSignupScreenName';
import type { SignupPartialView } from '~/sanity/types';
import { screenComponentMap } from './screenComponentMap';

const props = defineProps<{
  signupScreen: SignupScreen;
  sessionInfo: UnifiedSignupSessionInfo;
  onUpdatePage: () => void | Promise<void>;
  onHideNextButton: () => void;
  onSetEmailStatus: (status: string) => void;
  signupSurveyDiv?: HTMLDivElement | null;

  onHideBackButton: () => void;
}>();

const screenPartialData = ref<SignupPartialView>();

async function fetchScreenPartialData(
  screenName: UnifiedSignupScreenName
): Promise<SignupPartialView> {
  const data = await useRefsStore().getUssPartialData(screenName);
  return data as SignupPartialView;
}

// Watch for screen changes and update data (immediate: true handles initial fetch)
watch(
  () => props.signupScreen.screenName,
  async (newScreenName) => {
    screenPartialData.value = await fetchScreenPartialData(newScreenName);
  },
  { immediate: true }
);

const currentComponent = computed(() => screenComponentMap[props.signupScreen.screenName]);

const screenPartialDataWithRemainingComponents = computed(() => {
  const data = screenPartialData.value?.signupPartialViewsSectionBuilder;
  if (!data) {
    return undefined;
  }
  return {
    ...data,
    sectionBuilder: data.sectionBuilder?.slice(1) ?? [],
  };
});
</script>
